/**
 * A field label with a hover/focus help tooltip.
 *
 * Pass as a field's `label` prop:  label={<LabelWithHelp label="X" help="…" />}.
 * The label text stays the field's accessible name; the info icon is
 * keyboard-focusable so the tooltip is reachable without a mouse. Critical,
 * must-read guidance should still live in the field's `description` prop —
 * tooltips are for the secondary "what/when/recommendation" detail.
 */

import type { ReactNode } from "react";
import { Box, Text, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";

interface Props {
  label: string;
  help: ReactNode;
}

export function LabelWithHelp({ label, help }: Props) {
  return (
    <Box component="span" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {label}
      <Tooltip
        label={help}
        multiline
        w={320}
        withArrow
        events={{ hover: true, focus: true, touch: true }}
      >
        <Text
          component="span"
          c="dimmed"
          style={{ display: "inline-flex", cursor: "help" }}
          tabIndex={0}
          role="img"
          aria-label="More information"
        >
          <IconInfoCircle size={14} aria-hidden />
        </Text>
      </Tooltip>
    </Box>
  );
}
